import * as React from 'react';
import { Link } from "react-router-dom";
import { useQuery } from '../../Components/Query';
import { Column, Table } from "../../Components/QueryTable";
import { Authorization } from "../../misc/components/Authorization";
import { useSecurityContext } from "../../Core/SecurityContext";
import { PermissionEnum } from "../../misc/api/ApiPermissionEnum";
import { MatchSortHeader } from '../../Components/MatchSortHeader';
import { useState } from 'react';
import { useEffect } from 'react';
import { WebPortalClient, PopulationGroup, Study } from 'webportalclient';
import { ErrorBoundary } from "../../errors/ErrorBoundary";
import { LocalizedDateTime, DateTimeAccuracy } from '../../misc/components/LocalizedDateTime';
import Condition from '../../misc/components/Condition';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faBars } from '@fortawesome/free-solid-svg-icons';
import { uniqBy } from "lodash";

export const PopulationGroupsTable = () => {
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const { data, applyFilters, refresh } = useQuery();
    const client = useWebPortalApiClient();
    const [nameOptions, setNameOptions] = useState([]);
    const [raceOptions, setRaceOptions] = useState([]);

    useEffect(() => {

        const getFilterOptions = async () => {

            const popGroupNameResult = await client.client.getPopulationGroups('name,race,raceName', `studyId eq ${study.id}`);
            const nameOptions = popGroupNameResult.value.map(p => ({
                value: p.name,
                label: p.name,
            }));

            setNameOptions(nameOptions);

            const raceOptions = uniqBy(popGroupNameResult.value, g => g.race).map(p => ({
                value: p.race,
                label: p.raceName,
            }));

            setRaceOptions(raceOptions);
        };

        getFilterOptions();
    }, []);

    const importDefaults = async () => {

        const existingPopGroups = await client.client.getPopulationGroups(undefined, `studyId eq ${study.id}`);

        if (existingPopGroups["@odata.count"] === 0) {
            await client.client.postPopulationGroup({
                name: "White / Caucasian",
                race: "CAUCASIAN",
                predictedSetId: "PS_GLI_C",
                correctionFactor: 1.0,
                studyId: study.id,
            });

            await client.client.postPopulationGroup({
                name: "North East Asian",
                race: "ASIAN",
                predictedSetId: "PS_GLI_NEA",
                correctionFactor: 1.0,
                studyId: study.id,
            });

            await client.client.postPopulationGroup({
                name: "South East Asian",
                race: "ASIAN",
                predictedSetId: "PS_GLI_SEA",
                correctionFactor: 1.0,
                studyId: study.id,
            });

            await client.client.postPopulationGroup({
                name: "Black / African American",
                race: "BLACK",
                predictedSetId: "PS_GLI_A",
                correctionFactor: 1.0,
                studyId: study.id,
            });

            await client.client.postPopulationGroup({
                name: "Native Hawaiian / Other Pacific Islander",
                race: "NATIVEHAWAIIAN",
                predictedSetId: "PS_GLI_OTH",
                correctionFactor: 1.0,
                studyId: study.id,
            });

            await client.client.postPopulationGroup({
                name: "American Indian / Alaska Native",
                race: "AMERICANINDIAN",
                predictedSetId: "PS_GLI_OTH",
                correctionFactor: 1.0,
                studyId: study.id,
            });

            await client.client.postPopulationGroup({
                name: "Hispanic / Latino",
                race: "OTHER",
                predictedSetId: "PS_GLI_OTH",
                correctionFactor: 1.0,
                studyId: study.id,
            });

            await client.client.postPopulationGroup({
                name: "Other",
                race: "OTHER",
                predictedSetId: "PS_GLI_OTH",
                correctionFactor: 1.0,
                studyId: study.id,
            });

            refresh();
        }
    }

    const onDelete = async (id: string) => {
        await client.client.deletePopulationGroup(id);

        refresh();
    }

    const handleClearFilters = () => applyFilters([]);

    const menuFormatter = (popGroup: PopulationGroup) => {
        return (
            <Authorization permissionIdsAny={[PermissionEnum.EditPopulationGroups, PermissionEnum.DeletePopulationGroups]} studyId={study.id} currentPermissions={currentUserPermissions}>
                <div className="text-center">
                    <div className="btn-group" role="group">
                        <button type="button" className="btn btn-primary btn-xs dropdown-toggle" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                            <FontAwesomeIcon aria-hidden="true" icon={faBars} />
                        </button>
                        <ul className="dropdown-menu scrollable-menu" role="menu">
                            <Authorization
                                permissionId={PermissionEnum.EditPopulationGroups}
                                studyId={study.id}
                                currentPermissions={currentUserPermissions}
                            >
                                <li>
                                    <Link to={`/studies/populationgroups/edit/${popGroup.id}`}>Edit</Link>
                                </li>
                            </Authorization>
                            <Authorization
                                permissionId={PermissionEnum.DeletePopulationGroups}
                                studyId={study.id}
                                currentPermissions={currentUserPermissions}>
                                <li>
                                    <a onClick={() => onDelete(popGroup.id)}>Delete</a>
                                </li>
                            </Authorization>
                        </ul>
                    </div>
                </div>
            </Authorization>
        );
    };

    return (
        <ErrorBoundary>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Population Groups</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <Authorization studyId={study.id} currentPermissions={currentUserPermissions} permissionIdsAny={[PermissionEnum.CreatePopulationGroups]}>
                            <Link id="createNew" className="btn btn-primary" to="/studies/populationgroups/create">
                                Create New
                            </Link>&nbsp;
                            <button className="btn btn-primary" disabled={data.length > 0} onClick={() => importDefaults()}>
                                Import Default Population Groups
                            </button>&nbsp;
                        </Authorization>
                        <button className="btn btn-primary" onClick={handleClearFilters}>Clear All Filters</button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel panel-default">
                        <div className="panel-body">
                            <div id="PopulationGroupsTable" className="table-responsive" data-automationid="PopulationGroupsTable">
                                <Table data={data}>
                                    <Column field="name" header={<MatchSortHeader title="Name" field="name" options={nameOptions} sortField="name" />} formatter={n => n} />
                                    <Column field="raceName" header={<MatchSortHeader title="Core Race" field="raceName" options={raceOptions} sortField="raceName" />} formatter={n => n} />
                                    <Column field="" formatter={menuFormatter} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </ErrorBoundary>
    );
};
